import React, { useState } from 'react';
import './ApiDocs.css';

const CodeBlock = ({ children }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="api-code-block">
      <button className="api-code-copy" onClick={handleCopy}>
        {copied ? 'Copied!' : 'Copy'}
      </button>
      <pre><code>{children}</code></pre>
    </div>
  );
};

const Table = ({ headers, rows }) => (
  <div className="api-table-wrap">
    <table className="api-table">
      <thead>
        <tr>
          {headers.map((h, i) => <th key={i}>{h}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>
            {row.map((cell, j) => <td key={j}>{cell}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const CurlBlock = ({ children }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="api-curl-block">
      <div className="api-curl-header">
        <span className="api-curl-label">cURL</span>
        <button className="api-curl-copy" onClick={handleCopy}>
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <pre><code>{children}</code></pre>
    </div>
  );
};

const BASE_URL = 'https://api.chaimate.club';

const Endpoint = ({ method, path, children }) => (
  <div className="api-endpoint">
    <div className="api-endpoint-header">
      <span className={`api-method api-method--${method.toLowerCase()}`}>{method}</span>
      <code className="api-path">{BASE_URL}{path}</code>
    </div>
    {children}
  </div>
);

export default function ApiDocs() {
  return (
    <div className="api-docs">
      <div className="api-docs-container">
        <header className="api-header">
          <h1>Synth API Documentation</h1>
          <p className="api-header-sub">Complete reference for the Synth conversational AI API</p>
          <div className="api-base-url">
            <span className="api-base-url-label">Base URL</span>
            <code>{BASE_URL}</code>
          </div>
        </header>

        <section className="api-section">
          <h2>Authentication</h2>
          <p>All requests must include the following header:</p>
          <Table
            headers={['Header', 'Value']}
            rows={[['X-API-Key', 'synth_...']]}
          />
        </section>

        <section className="api-section">
          <h2>User ID Format</h2>
          <p>All <code>user_id</code> values must follow the format <code>{'{company_name}'}_id</code> — your company name prefix followed by an underscore and an identifier.</p>
          <Table
            headers={['Company', 'Valid user_id', 'Invalid user_id']}
            rows={[
              ['synth', 'synth_001', 'user_001'],
              ['chaimate', 'chaimate_abc123', 'abc123'],
              ['acme', 'acme_9x8y7z', '9x8y7z'],
            ]}
          />
          <p>The same <code>user_id</code> always maps to the same conversation history, so use a consistent identifier per user.</p>
        </section>

        <section className="api-section">
          <h2>Endpoints</h2>

          {/* Account Details & Credits */}
          <Endpoint method="GET" path="/api-keys/me">
            <h3>Account Details & Remaining Credits</h3>
            <p>Returns your account info and remaining credits. Use this to check your balance or debug errors.</p>
            <h4>Headers</h4>
            <Table
              headers={['Key', 'Value']}
              rows={[['X-API-Key', 'synth_...']]}
            />
            <CurlBlock>{`curl ${BASE_URL}/api-keys/me \\
  -H "X-API-Key: synth_YOUR_API_KEY"`}</CurlBlock>
            <h4>Response</h4>
            <CodeBlock>{`{
  "status": true,
  "data": {
    "company_name": "Chaimate",
    "company_prefix": "chaimate",
    "credits": 42,
    "is_active": true,
    "user_id_format": "chaimate_{id}",
    "user_id_example": "chaimate_123"
  }
}`}</CodeBlock>
          </Endpoint>

          {/* List Available Personalities */}
          <Endpoint method="GET" path="/api/personalities">
            <h3>List Available Personalities</h3>
            <p>Returns the personality types you can pass in chat requests.</p>
            <h4>Headers</h4>
            <Table
              headers={['Key', 'Value']}
              rows={[['X-API-Key', 'synth_...']]}
            />
            <CurlBlock>{`curl -X GET ${BASE_URL}/api/personalities \\
  -H "X-API-Key: synth_YOUR_API_KEY"`}</CurlBlock>
            <h4>Response</h4>
            <CodeBlock>{`{
  "status": true,
  "data": [
    {
      "id": "astrologer",
      "name": "Astrologer",
      "description": "Vedic astrology expert"
    }
  ]
}`}</CodeBlock>
          </Endpoint>

          {/* Send a Chat Message */}
          <Endpoint method="POST" path="/chat/external/reply">
            <h3>Send a Chat Message</h3>
            <p>Sends a message and returns the AI reply. Costs <strong>1 credit</strong> per successful call.</p>
            <h4>Headers</h4>
            <Table
              headers={['Key', 'Value']}
              rows={[
                ['X-API-Key', 'synth_...'],
                ['Content-Type', 'application/json'],
              ]}
            />
            <h4>Body Parameters</h4>
            <Table
              headers={['Field', 'Type', 'Required', 'Description']}
              rows={[
                ['user_id', 'string', 'yes', 'Your user\'s identifier. Must follow {company_name}_id format e.g. synth_001'],
                ['messages', 'array', 'yes', 'Conversation messages. Supports both {"role","content"} and {"text"} formats'],
                ['personality', 'string', 'yes', 'Personality type to use. Currently: astrologer'],
                ['expert_name', 'string', 'no', 'Optional display name for the astrologer (e.g., "Astro Rahul"). When present, responses start with “Main Astro Rahul hoon…”'],
                ['additional_data', 'string', 'no', 'Optional extra context injected verbatim into the persona prompt (e.g., birth details or background).'],
              ]}
            />
            <p>If omitted, <code>expert_name</code> and <code>additional_data</code> are ignored and the default persona behavior is used.</p>
            <h4>Messages Format</h4>
            <p>Both formats are accepted:</p>
            <CodeBlock>{`// Format 1: role/content
{ "role": "user", "content": "Hello" }

// Format 2: text only
{ "text": "Hello" }`}</CodeBlock>

            <h4>Example Request</h4>
            <CodeBlock>{`{
  "user_id": "synth_001",
  "personality": "astrologer",
  "expert_name": "Astro Rahul",
  "additional_data": "Rahul (Male, DOB 18 February 2009, TOB 06:26 PM, POB Placepalayam, Tamil Nadu, India)",
  "messages": [
    { "role": "user", "content": "My name is Rahul. Can you tell me about my future?" }
  ]
}`}</CodeBlock>
            <CurlBlock>{`curl -X POST ${BASE_URL}/chat/external/reply \\
  -H "X-API-Key: synth_YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "user_id": "synth_001",
    "personality": "astrologer",
    "expert_name": "Astro Rahul",
    "additional_data": "Rahul (Male, DOB 18 February 2009, TOB 06:26 PM, POB Placepalayam, Tamil Nadu, India)",
    "messages": [
      { "role": "user", "content": "My name is Rahul. Can you tell me about my future?" }
    ]
  }'`}</CurlBlock>

            <h4>Response</h4>
            <CodeBlock>{`{
  "status": true,
  "message": "",
  "data": [
    { "text": "Rahul, the stars speak of great things ahead..." },
    { "text": "Tell me your birth date and I can share more." }
  ]
}`}</CodeBlock>
          </Endpoint>
        </section>

        <section className="api-section">
          <h2>Error Reference</h2>
          <Table
            headers={['Code', 'Message', 'Cause']}
            rows={[
              ['1023', 'Invalid or inactive API key', 'Wrong key or key has been revoked'],
              ['1025', 'user_id must start with your assigned company prefix', 'user_id does not follow {company_name}_id format'],
              ['1026', 'Not enough credits. Please contact support to top up.', 'Credits exhausted — contact us'],
              ['1027', 'Unknown personality. Please pass a valid personality type.', 'personality value not in supported list'],
              ['1006', 'Unauthorized', 'Missing X-API-Key header'],
              ['1021', 'Bad Request', 'Missing required fields (user_id, messages)'],
            ]}
          />

          <h4>Error Response Shape</h4>
          <CodeBlock>{`{
  "status": false,
  "error_code": 1026,
  "error_message": "Not enough credits. Please contact support to top up."
}`}</CodeBlock>
        </section>

        <footer className="api-footer">
          <p>&copy; {new Date().getFullYear()} Walm Innovations. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
}
